import type { ReactElement } from "react";
import { useSearchParams } from "react-router";

import { queryValue } from "../../app/navigation.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { totpRules } from "../../forms/view-model-rules.js";

import { EmailCodeOffer } from "./email-code-pages.js";

/** 認証アプリを使えなくなったときの案内（よくあるご質問の「スマートフォンの機種を変えた・認証アプリを消してしまった場合」と同じ手順）。 */
const lostAppText = "スマートフォンの機種変更などで認証アプリを使えなくなった場合は、上の「メールで確認コードを受け取る」でログインし、"
    + "アカウント設定で二段階認証を解除してから、新しい認証アプリを登録し直してください。"
    + "ご登録のメールアドレスも使えない場合は、お問い合わせください。";

/**
 * ログインの 2 段目（確認コード）。
 *
 * <b>戻り先と「ログインしたままにする」は URL で受け取り、隠し欄で送り返す</b>
 * （欄の名前は API が受ける ViewModel と同じ）。
 */
export function VerifyTotpPage(): ReactElement {
    usePageMeta("二段階認証");
    const form = useApiForm("/api/account/totp", totpRules);
    const [search] = useSearchParams();

    return (
        <div className="page max-w-md">
            <div className="panel">
                <h1 className="page-title">二段階認証</h1>
                <p className="page-lead">認証アプリに表示されている 6 桁の確認コードを入力してください。</p>

                <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
                    <FormSummary messages={form.summary} />

                    <input type="hidden" name="ReturnUrl" value={queryValue(search, "returnUrl") ?? ""} />
                    <input type="hidden" name="RememberMe" value={queryValue(search, "rememberMe") ?? "false"} />

                    <TextField
                        label="確認コード" name="Code" required
                        inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                        placeholder="000000" autoFocus
                        wrapperClassName="mb-7"
                        inputClassName="text-center text-2xl tracking-[0.4em]"
                        error={form.fieldError("Code")}
                    />

                    <button type="submit" className="btn-primary w-full py-3" disabled={form.busy}>
                        確認
                    </button>
                </form>

                <EmailCodeOffer lead="認証アプリが手元に無い場合は、メールで認証できます。" />

                {/* 操作の入口は文中のリンクにせず、短い説明とボタンを並べる（docs/01 §6）。手順はよくあるご質問と同じ */}
                <div className="panel-footer flex items-center justify-between gap-4" data-totp-lost="">
                    <p>{lostAppText}</p>
                    <a href="/Home/Contact" className="btn-secondary btn-sm shrink-0">お問い合わせ</a>
                </div>
            </div>
        </div>
    );
}
